import { env } from '../../utils/env';
import { beginSocialRedirect, shouldUseSocialRedirect } from './socialRedirect.web';

declare global {
	interface Window {
		AppleID?: {
			auth?: {
				init: (config: Record<string, unknown>) => void;
				signIn: () => Promise<{
					authorization?: {
						id_token?: string;
					};
				}>;
			};
		};
	}
}

const APPLE_SDK_SRC =
	'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

function getRequiredEnv(key: string): string {
	const value = env(key)?.trim();
	if (!value) {
		throw new Error(`Missing ${key} for Apple web login`);
	}
	return value;
}

function getOptionalEnv(key: string): string | undefined {
	try {
		return env(key)?.trim();
	} catch {
		return undefined;
	}
}

export function isAppleWebSignInConfigured(): boolean {
	return !!getOptionalEnv('APPLE_WEB_CLIENT_ID') && !!getOptionalEnv('APPLE_WEB_REDIRECT_URI');
}

function loadAppleSdk(): Promise<void> {
	if (typeof window === 'undefined') {
		return Promise.reject(new Error('No window (SSR)'));
	}

	if (window.AppleID?.auth) {
		return Promise.resolve();
	}

	return new Promise((resolve, reject) => {
		const existing = document.querySelector(
			`script[src="${APPLE_SDK_SRC}"]`,
		) as HTMLScriptElement | null;

		if (existing) {
			if (window.AppleID?.auth) {
				resolve();
				return;
			}

			existing.addEventListener('load', () => resolve(), { once: true });
			existing.addEventListener(
				'error',
				() => reject(new Error('Failed to load Apple Sign-In SDK')),
				{ once: true },
			);
			return;
		}

		const script = document.createElement('script');
		script.src = APPLE_SDK_SRC;
		script.async = true;
		script.defer = true;
		script.onload = () => resolve();
		script.onerror = () => reject(new Error('Failed to load Apple Sign-In SDK'));
		document.head.appendChild(script);
	});
}

export async function appleSignInWeb(): Promise<string> {
	// The installed app cannot get a popup's answer back; the page goes to Apple instead
	// and the login resumes from the return URL on the next boot.
	if (shouldUseSocialRedirect()) {
		return beginSocialRedirect('apple');
	}

	const clientId = getRequiredEnv('APPLE_WEB_CLIENT_ID');
	const redirectURI = getRequiredEnv('APPLE_WEB_REDIRECT_URI');

	await loadAppleSdk();

	const auth = window.AppleID?.auth;
	if (!auth) {
		throw new Error('Apple Sign-In SDK is unavailable');
	}

	auth.init({
		clientId,
		scope: 'name email',
		redirectURI,
		usePopup: true,
	});

	const response = await auth.signIn();
	const idToken = response?.authorization?.id_token;

	if (!idToken) {
		throw new Error('No Apple identity token received');
	}

	return idToken;
}

export const appleSignIn = appleSignInWeb;
export default appleSignInWeb;
